import type {DraftIssueProjectReference, DraftIssueReference} from './copilot-chat-types'

/**
 * Serializes draft issue projects for sending to the backend.
 *
 * Returns DraftIssueProjectReference[] (objects with id and title).
 *
 * @param projects - Array of project references to serialize
 * @returns Serialized projects as DraftIssueProjectReference[]
 */
export function serializeDraftIssueProjects(projects?: DraftIssueProjectReference[]): DraftIssueReference['projects'] {
  if (!projects || projects.length === 0) {
    return []
  }

  // Send as DraftIssueProjectReference[] (format with IDs)
  // Filter out projects without any meaningful data
  return projects.filter(project => Boolean(project.title) || Boolean(project.id))
}

/**
 * Deserializes draft issue projects received from the backend.
 *
 * Accepts two input formats:
 * - string[] (legacy format from older backend versions)
 * - DraftIssueProjectReference[] (new format with id and title)
 *
 * Always returns DraftIssueProjectReference[] for consistent internal representation.
 *
 * @param projects - Projects in either string[] or DraftIssueProjectReference[] format
 * @returns Array of DraftIssueProjectReference objects
 */
export function deserializeDraftIssueProjects(
  projects: string[] | DraftIssueProjectReference[] | undefined,
): DraftIssueProjectReference[] {
  if (!projects || projects.length === 0) {
    return []
  }

  return projects
    .filter(project => {
      if (typeof project === 'string') {
        return project.trim() !== ''
      }
      return Boolean(project.title) || Boolean(project.id)
    })
    .map(project => {
      if (typeof project === 'string') {
        // Legacy string format - convert to object with title only
        return {title: project}
      }
      // New object format - return as-is (preserves optional fields)
      return project
    })
}
